import { navigateTo } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { IconAccessibilityCircle } from '@ds/icons/IconAccessibilityCircle'
import { IconArrowDownRightInCircle } from '@ds/icons/IconArrowDownRightInCircle'
import { IconAvatar } from '@ds/icons/IconAvatar'
import { IconCommentWithSparkles } from '@ds/icons/IconCommentWithSparkles'
import { IconHome } from '@ds/icons/IconHome'
import { IconQuestionMarkInCircle } from '@ds/icons/IconQuestionMarkInCircle'
import { IconSearch } from '@ds/icons/IconSearch'
import { IconShoppingBag } from '@ds/icons/IconShoppingBag'

import type { Shortcut } from '~/composables/useKeyboardShortcuts/types'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { CMS } from '~/scopes/cms/routes-names'
import { ROUTE_NAMES } from '~/scopes/dashboard/constants'
import { HELP_CENTER } from '~/scopes/help-center/route-names'
import { HOME } from '~/scopes/home/route-names'
import { SEARCH_BAR_INPUT_DATA_QA } from '~/scopes/search-bar/SearchBar.constants'
import { useToggleSearchBar } from '~/scopes/search-bar/composables/useToggleSearchBar'
import { useIsShoppingAssistantWebEnabled } from '~/scopes/shopping-assistant/composables/useIsShoppingAssistantWebEnabled'
import { useShoppingAssistantWebSidebar } from '~/scopes/shopping-assistant/stores/useShoppingAssistantWebSidebar'

import translations from './useTheMainContentShortcuts.translations'

export function useTheMainContentShortcuts() {
  const i18n = useI18n()
  const isShoppingAssistantEnabled = useIsShoppingAssistantWebEnabled()
  const shoppingAssistantStore = useShoppingAssistantWebSidebar()
  const { openSearchBar } = useToggleSearchBar()

  const skipShortcuts = computed<Shortcut[]>(() => [
    {
      icon: IconArrowDownRightInCircle,
      label: i18n(translations.mainContent),
      codes: [],
      keys: [],
      modifiers: [],
      action: () => {
        window.location.hash = '#main-content'
      },
    },
    {
      icon: IconSearch,
      label: i18n(translations.search),
      codes: ['Slash'],
      keys: ['/'],
      modifiers: [],
      action: () => {
        openSearchBar()
        document
          .querySelector<HTMLInputElement>(
            `[data-qa="${SEARCH_BAR_INPUT_DATA_QA}"]`,
          )
          ?.focus()
      },
    },
  ])

  const pageShortcuts = computed<Shortcut[]>(() => [
    {
      icon: IconHome,
      label: i18n(translations.home),
      codes: ['KeyH'],
      keys: ['h'],
      modifiers: ['altKey'],
      action: () => {
        navigateTo({ name: HOME })
      },
    },
    {
      icon: IconShoppingBag,
      label: i18n(translations.cart),
      codes: ['KeyC'],
      keys: ['c'],
      modifiers: ['altKey'],
      action: () => {
        navigateTo({ name: CHECKOUT.CART })
      },
    },
    {
      icon: IconAvatar,
      label: i18n(translations.profile),
      codes: ['KeyP'],
      keys: ['p'],
      modifiers: ['altKey'],
      action: () => {
        navigateTo({ name: ROUTE_NAMES.DASHBOARD.KYC.PROFILE })
      },
    },
    {
      icon: IconQuestionMarkInCircle,
      label: i18n(translations.helpCenter),
      codes: ['KeyI'],
      keys: ['i'],
      modifiers: ['altKey'],
      action: () => {
        navigateTo({ name: HELP_CENTER.HOME })
      },
    },
    {
      icon: IconAccessibilityCircle,
      label: i18n(translations.accessibilityStatement),
      codes: ['KeyU'],
      keys: ['u'],
      modifiers: ['altKey'],
      action: () => {
        navigateTo({ name: CMS.STATEMENT })
      },
    },
    ...(isShoppingAssistantEnabled.value
      ? [
          {
            icon: IconCommentWithSparkles,
            label: i18n(translations.shoppingAssistant),
            codes: ['KeyS'],
            keys: ['s'],
            modifiers: ['altKey'],
            action: () => {
              shoppingAssistantStore.open({ entryPoint: 'keyboard_shortcut' })
            },
          } satisfies Shortcut,
        ]
      : []),
  ])

  return { skipShortcuts, pageShortcuts }
}
